/**
 * Analysis Editor UI
 * 
 * Main editor component for creating and modifying supply chain analyses
 * Requirements: 1.1-1.12, 2.1-2.7, 3.1-3.7, 5.1-5.7, 6.1-6.6
 */

import React, { useState, useEffect, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { EntityManagementUI } from './EntityManagementUI';
import { RelationshipManagementUI } from './RelationshipManagementUI';
import { FrictionManagementUI, FrictionManagementUIRef } from './FrictionManagementUI';
import { ScenarioManagementUI } from './ScenarioManagementUI';
import { BedrockAnalysisModal } from './BedrockAnalysisModal';
import { ForceDirectedGraph, GraphData, GraphNode, GraphEdge } from '../visualization/ForceDirectedGraph';
import { VisualizationModes } from '../visualization/VisualizationModes';

export interface Analysis {
  id: string;
  name: string;
  ownerId: string;
  visibility: 'public' | 'private';
  isTemplate: boolean;
  entities: any[];
  relationships: any[];
  frictions: any[];
  scenarios: any[];
  createdAt: string;
  updatedAt: string;
}

interface AnalysisEditorProps {
  analysisId: string;
  onSave?: (analysis: Analysis) => void;
}

type EditorTab = 'entities' | 'relationships' | 'frictions' | 'scenarios' | 'analysis' | 'visualization';

/**
 * Analysis Editor Component
 * 
 * Complete implementation with:
 * - Tab-based navigation
 * - Entity management integration
 * - Relationship management integration
 * - Friction management integration
 * - Scenario management integration
 * - Reverse causality analysis
 * - Save/load functionality
 * - Tension calculation trigger
 */
export const AnalysisEditor: React.FC<AnalysisEditorProps> = ({ analysisId, onSave }) => {
  const [activeTab, setActiveTab] = useState<EditorTab>('entities');
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [refreshingFrictions, setRefreshingFrictions] = useState(false);
  const [relationshipsWithFrictions, setRelationshipsWithFrictions] = useState<Map<string, any[]>>(new Map());
  const [tensionResult, setTensionResult] = useState<any>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
  const [analysisDocument, setAnalysisDocument] = useState<string | null>(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  
  const frictionManagementRef = useRef<FrictionManagementUIRef>(null);

  useEffect(() => {
    fetchAnalysis();
  }, [analysisId]);

  // Listen for navigation events from expandable lists
  useEffect(() => {
    const handleNavigateToEntity = (event: any) => {
      const { entityId } = event.detail;
      console.log('Navigate to entity:', entityId);
      // Switch to visualization tab
      setActiveTab('visualization');
      // TODO: Highlight entity in visualization
      // This will be handled by the visualization component
    };

    const handleNavigateToRelationship = (event: any) => {
      const { relationshipId } = event.detail;
      console.log('Navigate to relationship:', relationshipId);
      // Switch to visualization tab
      setActiveTab('visualization');
      // TODO: Highlight relationship in visualization
      // This will be handled by the visualization component
    };

    window.addEventListener('navigateToEntity', handleNavigateToEntity);
    window.addEventListener('navigateToRelationship', handleNavigateToRelationship);

    return () => {
      window.removeEventListener('navigateToEntity', handleNavigateToEntity);
      window.removeEventListener('navigateToRelationship', handleNavigateToRelationship);
    };
  }, []);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/analyses/${analysisId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch analysis');
      
      const data = await response.json();
      setAnalysis(data);
    } catch (err) {
      console.error('Error fetching analysis:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchAnalysis();
      alert('Analysis refreshed successfully');
    } catch (err) {
      console.error('Error refreshing analysis:', err);
      alert(err instanceof Error ? err.message : 'Failed to refresh analysis');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRename = async () => {
    if (!editedName.trim()) {
      alert('Analysis name cannot be empty');
      return;
    }

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/analyses/${analysisId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: editedName.trim() })
      });
      
      if (!response.ok) throw new Error('Failed to rename analysis');
      
      setIsEditingName(false);
      await fetchAnalysis();
    } catch (err) {
      console.error('Error renaming analysis:', err);
      alert(err instanceof Error ? err.message : 'Failed to rename analysis');
    }
  };

  const startEditing = () => {
    setEditedName(analysis?.name || '');
    setIsEditingName(true);
  };

  const cancelEditing = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleRefreshFrictions = async () => {
    if (activeTab !== 'frictions') {
      // Switch to frictions tab first
      setActiveTab('frictions');
      // Wait a bit for the component to mount, then try again
      setTimeout(async () => {
        if (frictionManagementRef.current) {
          try {
            setRefreshingFrictions(true);
            await frictionManagementRef.current.refreshFrictions();
          } catch (err) {
            console.error('Error refreshing frictions:', err);
            alert(err instanceof Error ? err.message : 'Failed to refresh frictions');
          } finally {
            setRefreshingFrictions(false);
          }
        }
      }, 100);
      return;
    }
    
    if (!frictionManagementRef.current) {
      alert('Friction management not loaded. Please try again.');
      return;
    }
    
    try {
      setRefreshingFrictions(true);
      await frictionManagementRef.current.refreshFrictions();
    } catch (err) {
      console.error('Error refreshing frictions:', err);
      alert(err instanceof Error ? err.message : 'Failed to refresh frictions');
    } finally {
      setRefreshingFrictions(false);
    }
  };

  const handleCalculateTension = async () => {
    try {
      setCalculating(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/analyses/${analysisId}/calculate-tension`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to calculate tension');
      
      const result = await response.json();
      
      console.log('Calculate tension response:', result);
      console.log('Risk scores from response:', result.result?.riskScores);
      console.log('relationshipsWithFrictions:', result.result?.relationshipsWithFrictions);
      
      alert('Tension calculation completed successfully. Risk scores have been updated.');
      
      // Store the full tension result
      if (result.result) {
        setTensionResult(result.result);
      }
      
      // Store relationshipsWithFrictions data for the visualization
      if (result.result?.relationshipsWithFrictions) {
        const frictionsMap = new Map();
        result.result.relationshipsWithFrictions.forEach((rf: any) => {
          console.log('Processing relationship friction:', rf);
          // Store the entire object including tensionBreakdown
          frictionsMap.set(rf.id, rf);
        });
        console.log('Frictions map created:', frictionsMap);
        
        // Update state directly instead of using event
        setRelationshipsWithFrictions(frictionsMap);
        
        // Also trigger event for any other listeners
        window.dispatchEvent(new CustomEvent('relationshipFrictionsUpdated', { 
          detail: frictionsMap 
        }));
      }
      
      // Trigger entity refresh event so EntityManagementUI can refetch
      window.dispatchEvent(new CustomEvent('entitiesNeedRefresh'));
      
      fetchAnalysis(); // Refresh to get updated tension values
    } catch (err) {
      console.error('Error calculating tension:', err);
      alert(err instanceof Error ? err.message : 'Failed to calculate tension');
    } finally {
      setCalculating(false);
    }
  };

  const handleGenerateBedrockAnalysis = async () => {
    try {
      setGeneratingAnalysis(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      // Start the job
      const startResponse = await fetch(
        `${apiUrl}/analyses/${analysisId}/generate-bedrock-analysis`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!startResponse.ok) {
        const error = await startResponse.json();
        throw new Error(error.error?.message || 'Failed to start analysis');
      }
      
      const { jobId } = await startResponse.json();
      console.log('Bedrock analysis job started:', jobId);
      
      // Poll for results
      const pollInterval = 2000; // 2 seconds
      const maxAttempts = 60; // 2 minutes max
      let attempts = 0;
      
      const poll = async (): Promise<void> => {
        attempts++;
        
        if (attempts > maxAttempts) {
          throw new Error('Analysis timed out. Please try again.');
        }
        
        const statusResponse = await fetch(
          `${apiUrl}/analyses/${analysisId}/generate-bedrock-analysis?jobId=${jobId}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (!statusResponse.ok) {
          const error = await statusResponse.json();
          throw new Error(error.error?.message || 'Failed to check job status');
        }
        
        const result = await statusResponse.json();
        
        if (result.status === 'completed') {
          setAnalysisDocument(result.document);
          setShowAnalysisModal(true);
          setGeneratingAnalysis(false);
        } else if (result.status === 'failed') {
          throw new Error(result.error || 'Analysis failed');
        } else {
          // Still processing, poll again
          setTimeout(poll, pollInterval);
        }
      };
      
      // Start polling
      await poll();
    } catch (err) {
      console.error('Error generating Bedrock analysis:', err);
      setGeneratingAnalysis(false);
      alert(err instanceof Error ? err.message : 'Failed to generate AI analysis');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: '18px', marginBottom: '8px' }}>Loading analysis...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ padding: '16px', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', color: '#c00' }}>
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="analysis-editor" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '2px solid #d1d5db',
        background: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ flex: 1 }}>
          {isEditingName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRename();
                  if (e.key === 'Escape') cancelEditing();
                }}
                autoFocus
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  padding: '4px 8px',
                  border: '2px solid #3b82f6',
                  borderRadius: '4px',
                  outline: 'none',
                  minWidth: '300px'
                }}
              />
              <button
                onClick={handleRename}
                style={{
                  padding: '6px 12px',
                  background: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                Save
              </button>
              <button
                onClick={cancelEditing}
                style={{
                  padding: '6px 12px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h2 style={{ margin: '0' }}>{analysis?.name || 'Analysis Editor'}</h2>
              <button
                onClick={startEditing}
                style={{
                  padding: '4px 8px',
                  background: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
                title="Rename analysis"
              >
                ✏️ Rename
              </button>
            </div>
          )}
          <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
            Last updated: {analysis?.updatedAt ? new Date(analysis.updatedAt).toLocaleString() : 'N/A'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleCalculateTension}
            disabled={calculating}
            style={{
              padding: '10px 20px',
              background: '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: calculating ? 'not-allowed' : 'pointer',
              fontWeight: '500',
            }}
          >
            {calculating ? 'Calculating...' : '⚡ Calculate Tension'}
          </button>
          <button
            onClick={handleGenerateBedrockAnalysis}
            disabled={generatingAnalysis}
            style={{
              padding: '10px 20px',
              background: generatingAnalysis ? '#9ca3af' : '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: generatingAnalysis ? 'not-allowed' : 'pointer',
              fontWeight: '500',
            }}
          >
            {generatingAnalysis ? 'Generating...' : '🤖 Generate AI Analysis'}
          </button>
          <button
            onClick={handleRefreshFrictions}
            disabled={refreshingFrictions}
            style={{
              padding: '10px 20px',
              background: refreshingFrictions ? '#9ca3af' : '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: refreshingFrictions ? 'not-allowed' : 'pointer',
              fontWeight: '500',
            }}
            title="Re-evaluate all friction formulas against current entities and relationships"
          >
            {refreshingFrictions ? 'Refreshing...' : '🔄 Refresh All Frictions'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              padding: '10px 20px',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              fontWeight: '500',
            }}
          >
            {refreshing ? 'Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {/* Editor Tabs */}
      <div className="editor-tabs" style={{
        display: 'flex',
        gap: '4px',
        padding: '8px 16px',
        borderBottom: '1px solid #d1d5db',
        background: '#f9fafb',
      }}>
        <TabButton
          active={activeTab === 'entities'}
          onClick={() => setActiveTab('entities')}
          label="📦 Entities"
        />
        <TabButton
          active={activeTab === 'relationships'}
          onClick={() => setActiveTab('relationships')}
          label="🔗 Relationships"
        />
        <TabButton
          active={activeTab === 'frictions'}
          onClick={() => setActiveTab('frictions')}
          label="⚖️ Friction"
        />
        {/* Scenarios tab hidden - stub implementation
        <TabButton
          active={activeTab === 'scenarios'}
          onClick={() => setActiveTab('scenarios')}
          label="🎭 Scenarios"
        />
        */}
        <TabButton
          active={activeTab === 'analysis'}
          onClick={() => setActiveTab('analysis')}
          label="🔍 Analysis"
        />
        <TabButton
          active={activeTab === 'visualization'}
          onClick={() => setActiveTab('visualization')}
          label="📊 Visualization"
        />
      </div>

      {/* Editor Content */}
      <div className="editor-content" style={{
        flex: 1,
        padding: '20px',
        overflowY: 'auto',
        background: '#fafafa',
      }}>
        {activeTab === 'entities' && <EntityManagementUI analysisId={analysisId} />}
        {activeTab === 'relationships' && <RelationshipManagementUI analysisId={analysisId} />}
        {activeTab === 'frictions' && <FrictionManagementUI ref={frictionManagementRef} analysisId={analysisId} />}
        {/* Scenarios tab hidden - stub implementation
        {activeTab === 'scenarios' && <ScenarioManagementUI analysisId={analysisId} />}
        */}
        {activeTab === 'analysis' && <ReverseCausalityAnalysisUI analysisId={analysisId} />}
        {activeTab === 'visualization' && (
          <VisualizationUI 
            analysisId={analysisId} 
            relationshipsWithFrictions={relationshipsWithFrictions}
            setRelationshipsWithFrictions={setRelationshipsWithFrictions}
            tensionResult={tensionResult}
          />
        )}
      </div>

      {/* Bedrock Analysis Modal */}
      {showAnalysisModal && analysisDocument && (
        <BedrockAnalysisModal
          document={analysisDocument}
          analysisName={analysis?.name || 'Analysis'}
          onClose={() => setShowAnalysisModal(false)}
        />
      )}
    </div>
  );
};

/**
 * Visualization UI Component
 * 
 * Displays multiple visualization modes for the supply chain
 */
const VisualizationUI: React.FC<{ 
  analysisId: string;
  relationshipsWithFrictions: Map<string, any[]>;
  setRelationshipsWithFrictions: React.Dispatch<React.SetStateAction<Map<string, any[]>>>;
  tensionResult?: any;
}> = ({ analysisId, relationshipsWithFrictions, setRelationshipsWithFrictions, tensionResult }) => {
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], edges: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visualizationMode, setVisualizationMode] = useState<'force-directed' | 'data-flow'>('force-directed');
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null);
  const [frictions, setFrictions] = useState<any[]>([]);
  const [showDataTypeIndicators, setShowDataTypeIndicators] = useState(true);

  useEffect(() => {
    fetchGraphData();
    
    // Listen for relationship frictions updates from calculate tension
    const handleFrictionsUpdate = (event: any) => {
      setRelationshipsWithFrictions(event.detail);
      // Don't call fetchGraphData here - let the useEffect below handle it
    };
    
    window.addEventListener('relationshipFrictionsUpdated', handleFrictionsUpdate);
    
    return () => {
      window.removeEventListener('relationshipFrictionsUpdated', handleFrictionsUpdate);
    };
  }, [analysisId]);

  // Separate effect to refetch graph data when relationshipsWithFrictions changes
  useEffect(() => {
    if (relationshipsWithFrictions.size > 0) {
      console.log('relationshipsWithFrictions updated, refetching graph data');
      fetchGraphData();
    }
  }, [relationshipsWithFrictions]);

  const fetchGraphData = async () => {
    try {
      setLoading(true);
      setError(null);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';

      // Fetch entities
      const entitiesResponse = await fetch(`${apiUrl}/analyses/${analysisId}/entities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!entitiesResponse.ok) throw new Error('Failed to fetch entities');
      const entitiesData = await entitiesResponse.json();
      const entities = Array.isArray(entitiesData) ? entitiesData : (entitiesData.entities || []);

      // Fetch relationships
      const relationshipsResponse = await fetch(`${apiUrl}/analyses/${analysisId}/relationships`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!relationshipsResponse.ok) throw new Error('Failed to fetch relationships');
      const relationshipsData = await relationshipsResponse.json();
      const relationships = Array.isArray(relationshipsData) ? relationshipsData : (relationshipsData.relationships || []);

      // Fetch tension calculation results
      const analysisResponse = await fetch(`${apiUrl}/analyses/${analysisId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!analysisResponse.ok) throw new Error('Failed to fetch analysis');
      const analysis = await analysisResponse.json();

      // Fetch frictions
      const frictionsResponse = await fetch(`${apiUrl}/analyses/${analysisId}/frictions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!frictionsResponse.ok) throw new Error('Failed to fetch frictions');
      const frictionsData = await frictionsResponse.json();
      const fetchedFrictions = Array.isArray(frictionsData) ? frictionsData : (frictionsData.frictions || []);
      setFrictions(fetchedFrictions);

      // Transform to graph format
      const nodes = entities.map((entity: any) => ({
        id: entity.id,
        name: entity.name,
        dataTypes: entity.dataTypes || [],
        naicsCodes: entity.naicsCodes || [],
        regulatoryCategories: entity.regulatoryCategories || [],
        riskScore: analysis.riskScores?.[entity.id] || 0,
        entityJurisdiction: entity.entityJurisdiction,
      }));
      
      console.log('[VISUALIZATION] Analysis risk scores:', analysis.riskScores);
      console.log('[VISUALIZATION] Mapped nodes:', nodes.map((n: any) => ({ id: n.id, name: n.name, riskScore: n.riskScore })));

      const edges = relationships.map((rel: any) => {
        const rfData = relationshipsWithFrictions.get(rel.id) as any;
        return {
          id: rel.id,
          source: rel.sourceEntityId,
          target: rel.targetEntityId,
          relationshipType: rel.relationshipTypes?.join(', ') || rel.relationshipType || 'Not specified',
          financialImpact: rel.financialImpact || 0,
          description: rel.description,
          tension: rel.tension || { sourceToTarget: 0, targetToSource: 0 },
          maxTension: Math.max(
            rel.tension?.sourceToTarget || 0,
            rel.tension?.targetToSource || 0
          ),
          contributingFrictions: rfData?.contributingFrictions || rfData || [],
          tensionBreakdown: rfData?.tensionBreakdown
        };
      });

      setGraphData({ nodes, edges });
    } catch (err) {
      console.error('Error fetching graph data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load visualization data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: '18px', marginBottom: '8px' }}>Loading visualization...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ padding: '16px', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', color: '#c00' }}>
          <strong>Error:</strong> {error}
        </div>
        <button
          onClick={fetchGraphData}
          style={{
            marginTop: '12px',
            padding: '8px 16px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (graphData.nodes.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
        <div style={{ fontSize: '16px', marginBottom: '8px' }}>No data to visualize</div>
        <div style={{ fontSize: '14px' }}>Add entities and relationships to see the visualization</div>
      </div>
    );
  }

  return (
    <div className="visualization-ui" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      width: '100%',
      overflow: 'hidden'
    }}>
      <div style={{ 
        padding: '16px 20px',
        borderBottom: '1px solid #d1d5db',
        background: 'white',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>Supply Chain Network Visualization</h3>
          <button
            onClick={fetchGraphData}
            style={{
              padding: '8px 16px',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            🔄 Refresh
          </button>
        </div>

        {/* Mode Selector */}
        <div style={{
          display: 'flex',
          gap: '4px',
          flexWrap: 'wrap',
        }}>
          <ModeButton mode="force-directed" currentMode={visualizationMode} onClick={setVisualizationMode} label="Force Graph" />
          <ModeButton mode="data-flow" currentMode={visualizationMode} onClick={setVisualizationMode} label="Flow" />
        </div>
      </div>

      <div style={{ 
        padding: '12px 20px',
        background: '#e0f2fe',
        border: '1px solid #7dd3fc',
        fontSize: '14px',
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <strong>Tip:</strong> {visualizationMode === 'force-directed' 
            ? 'Drag nodes to reposition them. Scroll to zoom. Click nodes or edges for details.'
            : 'Use the mode selector above to switch between different visualization types. Each view provides unique insights into your supply chain.'}
        </div>
        {visualizationMode === 'force-directed' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginLeft: '16px', whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={showDataTypeIndicators}
              onChange={(e) => setShowDataTypeIndicators(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>Show Data Types</span>
          </label>
        )}
      </div>

      <div style={{ 
        display: 'flex',
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main visualization area */}
        <div style={{ 
          flex: 1,
          height: '700px',
          overflow: 'auto',
          background: '#fafafa',
          position: 'relative'
        }}>
          {visualizationMode === 'force-directed' ? (
            <ForceDirectedGraphResponsive
              data={graphData}
              showDataTypeIndicators={showDataTypeIndicators}
              onNodeClick={(nodeId) => {
                setSelectedNode(nodeId || null);
                setSelectedEdge(null);
              }}
              onEdgeClick={(edgeId) => {
                setSelectedEdge(edgeId || null);
                setSelectedNode(null);
              }}
            />
          ) : (
            <VisualizationModes
              data={graphData}
              mode={visualizationMode}
              onModeChange={setVisualizationMode}
            />
          )}
        </div>

        {/* Details panel */}
        {(selectedNode || selectedEdge) && (
          <div style={{
            width: '320px',
            height: '700px',
            borderLeft: '2px solid #d1d5db',
            background: 'white',
            overflow: 'auto',
            flexShrink: 0
          }}>
            {selectedNode && (
              <NodeDetailsPanel 
                node={graphData.nodes.find(n => n.id === selectedNode)!}
                tensionResult={tensionResult}
                onClose={() => setSelectedNode(null)}
              />
            )}
            {selectedEdge && (
              <EdgeDetailsPanel 
                edge={graphData.edges.find(e => e.id === selectedEdge)!}
                nodes={graphData.nodes}
                frictions={frictions}
                tensionResult={tensionResult}
                onClose={() => setSelectedEdge(null)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ModeButton: React.FC<{
  mode: 'force-directed' | 'data-flow';
  currentMode: 'force-directed' | 'data-flow';
  onClick: (mode: 'force-directed' | 'data-flow') => void;
  label: string;
}> = ({ mode, currentMode, onClick, label }) => (
  <button
    onClick={() => onClick(mode)}
    style={{
      padding: '6px 12px',
      background: mode === currentMode ? '#3b82f6' : 'white',
      color: mode === currentMode ? 'white' : '#333',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: mode === currentMode ? '600' : '400',
    }}
  >
    {label}
  </button>
);

/**
 * Responsive Force-Directed Graph wrapper
 */
const ForceDirectedGraphResponsive: React.FC<{
  data: GraphData;
  showDataTypeIndicators: boolean;
  onNodeClick?: (nodeId: string) => void;
  onEdgeClick?: (edgeId: string) => void;
}> = ({ data, showDataTypeIndicators, onNodeClick, onEdgeClick }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = React.useState({ width: 1200, height: 660 });

  React.useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newWidth = Math.max(rect.width - 40, 400);
        const newHeight = 660; // Fixed height
        setDimensions({ width: newWidth, height: newHeight });
      }
    };

    // Initial update with a small delay to ensure container is rendered
    const timer = setTimeout(updateDimensions, 100);
    
    // Update on window resize
    window.addEventListener('resize', updateDimensions);
    
    // Use ResizeObserver for better responsiveness
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateDimensions);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} style={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      padding: '20px',
      boxSizing: 'border-box'
    }}>
      <ForceDirectedGraph
        data={data}
        width={dimensions.width}
        height={dimensions.height}
        showDataTypeIndicators={showDataTypeIndicators}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
      />
    </div>
  );
};

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
}> = ({ active, onClick, label }) => (
  <button
    onClick={onClick}
    style={{
      padding: '10px 20px',
      background: active ? 'white' : 'transparent',
      color: active ? '#3b82f6' : '#666',
      border: active ? '1px solid #d1d5db' : '1px solid transparent',
      borderBottom: active ? '1px solid white' : '1px solid transparent',
      borderRadius: '8px 8px 0 0',
      cursor: 'pointer',
      fontWeight: active ? '600' : '400',
      fontSize: '14px',
      marginBottom: '-1px',
    }}
  >
    {label}
  </button>
);

/**
 * Reverse Causality Analysis UI (Requirements: 6.1-6.6)
 * 
 * Allows users to:
 * - Select conflict hotspots
 * - Analyze upstream contributing factors
 * - View friction decomposition
 * - Identify root causes
 */
const ReverseCausalityAnalysisUI: React.FC<{ analysisId: string }> = ({ analysisId }) => {
  const [hotspots, setHotspots] = useState<any[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [relationships, setRelationships] = useState<any[]>([]);
  const [selectedHotspot, setSelectedHotspot] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [analysisId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';

      // Fetch hotspots
      const hotspotsResponse = await fetch(`${apiUrl}/analyses/${analysisId}/hotspots`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!hotspotsResponse.ok) throw new Error('Failed to fetch hotspots');
      const hotspotsData = await hotspotsResponse.json();
      setHotspots(hotspotsData.hotspots || []);

      // Fetch entities
      const entitiesResponse = await fetch(`${apiUrl}/analyses/${analysisId}/entities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!entitiesResponse.ok) throw new Error('Failed to fetch entities');
      const entitiesData = await entitiesResponse.json();
      setEntities(Array.isArray(entitiesData) ? entitiesData : (entitiesData.entities || []));

      // Fetch relationships
      const relationshipsResponse = await fetch(`${apiUrl}/analyses/${analysisId}/relationships`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!relationshipsResponse.ok) throw new Error('Failed to fetch relationships');
      const relationshipsData = await relationshipsResponse.json();
      setRelationships(Array.isArray(relationshipsData) ? relationshipsData : (relationshipsData.relationships || []));
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getHotspotName = (hotspot: any) => {
    if (hotspot.type === 'entity') {
      const entity = entities.find(e => e.id === hotspot.id);
      return entity ? entity.name : 'Unknown Entity';
    } else {
      const relationship = relationships.find(r => r.id === hotspot.id);
      if (relationship) {
        const sourceEntity = entities.find(e => e.id === relationship.sourceEntityId);
        const targetEntity = entities.find(e => e.id === relationship.targetEntityId);
        return `${sourceEntity?.name || 'Unknown'} → ${targetEntity?.name || 'Unknown'}`;
      }
      return 'Unknown Relationship';
    }
  };

  const handleAnalyzeHotspot = async (hotspotId: string) => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/analyses/${analysisId}/hotspots/${hotspotId}/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to analyze hotspot');
      
      const data = await response.json();
      setAnalysis(data);
      setSelectedHotspot(hotspotId);
    } catch (err) {
      console.error('Error analyzing hotspot:', err);
      alert(err instanceof Error ? err.message : 'Failed to analyze hotspot');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reverse-causality-analysis-ui">
      <h3 style={{ marginBottom: '16px' }}>Reverse Causality Analysis</h3>
      
      <div style={{ marginBottom: '16px', padding: '12px', background: '#e0f2fe', border: '1px solid #7dd3fc', borderRadius: '8px', fontSize: '14px' }}>
        <strong>About:</strong> Select a conflict hotspot to analyze its upstream contributing factors and identify root causes of tension.
      </div>

      {/* Hotspot List */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ marginBottom: '12px' }}>Conflict Hotspots</h4>
        
        {loading && hotspots.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            Loading hotspots...
          </div>
        )}

        {!loading && hotspots.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            No conflict hotspots detected. Try calculating tension first.
          </div>
        )}

        <div style={{ display: 'grid', gap: '12px' }}>
          {hotspots.map(hotspot => {
            const displayLevel = hotspot.tensionLevel ?? 0;
            const hotspotName = getHotspotName(hotspot);
            
            return (
              <div key={hotspot.id} style={{
                border: `2px solid ${selectedHotspot === hotspot.id ? '#3b82f6' : '#d1d5db'}`,
                borderRadius: '8px',
                padding: '12px',
                background: 'white',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <h5 style={{ margin: '0 0 4px 0' }}>
                      {hotspot.type === 'entity' ? '📦 Entity' : '🔗 Relationship'} Hotspot
                    </h5>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      {hotspotName}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      Tension Level: <span style={{
                        color: displayLevel > 7 ? '#ef4444' : displayLevel > 4 ? '#f59e0b' : '#22c55e',
                        fontWeight: '600',
                      }}>
                        {displayLevel.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAnalyzeHotspot(hotspot.id)}
                    disabled={loading}
                    style={{
                      padding: '6px 12px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      flexShrink: 0,
                      marginLeft: '12px',
                    }}
                  >
                    {loading ? 'Analyzing...' : 'Analyze'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Analysis Results */}
      {analysis && (
        <div style={{
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          padding: '16px',
          background: '#f0f9ff',
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '16px' }}>Analysis Results</h4>
          
          {/* Contributing Factors */}
          {analysis.contributingFactors && analysis.contributingFactors.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h5 style={{ marginBottom: '8px' }}>Contributing Factors:</h5>
              <div style={{ display: 'grid', gap: '8px' }}>
                {analysis.contributingFactors.map((factor: any, idx: number) => (
                  <div key={idx} style={{
                    background: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    padding: '8px',
                    fontSize: '13px',
                  }}>
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>{factor.name}</div>
                    <div style={{ color: '#666' }}>
                      Impact: <span style={{ fontWeight: '600' }}>{factor.impact.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Friction Decomposition */}
          {analysis.frictionDecomposition && (
            <div style={{ marginBottom: '16px' }}>
              <h5 style={{ marginBottom: '8px' }}>Friction Decomposition:</h5>
              <div style={{ background: 'white', border: '1px solid #d1d5db', borderRadius: '4px', padding: '12px', fontSize: '13px' }}>
                {Object.entries(analysis.frictionDecomposition).map(([friction, value]: [string, any]) => (
                  <div key={friction} style={{ marginBottom: '4px' }}>
                    <strong>{friction}:</strong> {value.toFixed(2)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Root Causes */}
          {analysis.rootCauses && analysis.rootCauses.length > 0 && (
            <div>
              <h5 style={{ marginBottom: '8px' }}>Root Causes:</h5>
              <ul style={{ marginLeft: '20px', fontSize: '13px' }}>
                {analysis.rootCauses.map((cause: string, idx: number) => (
                  <li key={idx} style={{ marginBottom: '4px' }}>{cause}</li>
                ))}
              </ul>
            </div>
          )}

          <button
            onClick={() => {
              setAnalysis(null);
              setSelectedHotspot(null);
            }}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Close Analysis
          </button>
        </div>
      )}
    </div>
  );
};


/**
 * Node Details Panel
 * Shows detailed information about a selected node
 */
const NodeDetailsPanel: React.FC<{
  node: GraphNode;
  tensionResult?: any;
  onClose: () => void;
}> = ({ node, tensionResult, onClose }) => {
  // Get risk score from tension result, fallback to node.riskScore
  const riskScore = tensionResult?.riskScores?.[node.id] ?? node.riskScore ?? 0;
  
  return (
  <div style={{ padding: '16px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
      <h4 style={{ margin: 0 }}>Entity Details</h4>
      <button
        onClick={onClose}
        style={{
          background: 'transparent',
          border: 'none',
          fontSize: '20px',
          cursor: 'pointer',
          padding: '4px 8px',
        }}
      >
        ×
      </button>
    </div>

    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
        {node.name}
      </div>
      <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
        ID: {node.id}
      </div>
    </div>

    {/* Risk Score */}
    <div style={{ marginBottom: '16px', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
      <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Risk Score</div>
      <div style={{ fontSize: '24px', fontWeight: '700', color: riskScore > 100 ? '#ef4444' : riskScore > 50 ? '#f59e0b' : '#22c55e' }}>
        {riskScore.toFixed(2)}
      </div>
      <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>
        (Entity Friction + Vulnerability + Incoming Dependency Risks)
      </div>
    </div>

    {/* Jurisdiction */}
    {node.entityJurisdiction && (
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Jurisdiction</div>
        <div style={{ fontSize: '14px' }}>{node.entityJurisdiction}</div>
      </div>
    )}

    {/* Data Types */}
    {node.dataTypes.length > 0 && (
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Data Types</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {node.dataTypes.map((type, idx) => (
            <span
              key={idx}
              style={{
                padding: '4px 8px',
                background: '#e0f2fe',
                border: '1px solid #7dd3fc',
                borderRadius: '4px',
                fontSize: '12px',
              }}
            >
              {type}
            </span>
          ))}
        </div>
      </div>
    )}

    {/* NAICS Codes */}
    {node.naicsCodes.length > 0 && (
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>NAICS Codes</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {node.naicsCodes.map((code, idx) => (
            <span
              key={idx}
              style={{
                padding: '4px 8px',
                background: '#f3e8ff',
                border: '1px solid #c084fc',
                borderRadius: '4px',
                fontSize: '12px',
              }}
            >
              {code}
            </span>
          ))}
        </div>
      </div>
    )}

    {/* Regulatory Categories */}
    {node.regulatoryCategories.length > 0 && (
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Regulatory Categories</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {node.regulatoryCategories.map((category, idx) => (
            <span
              key={idx}
              style={{
                padding: '4px 8px',
                background: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '4px',
                fontSize: '12px',
              }}
            >
              {category}
            </span>
          ))}
        </div>
      </div>
    )}
  </div>
  );
};

/**
 * Edge Details Panel
 * Shows detailed information about a selected edge
 */
const EdgeDetailsPanel: React.FC<{
  edge: GraphEdge;
  nodes: GraphNode[];
  frictions: any[];
  tensionResult?: any;
  onClose: () => void;
}> = ({ edge, nodes, frictions, tensionResult, onClose }) => {
  const sourceNode = nodes.find(n => n.id === (typeof edge.source === 'string' ? edge.source : edge.source.id));
  const targetNode = nodes.find(n => n.id === (typeof edge.target === 'string' ? edge.target : edge.target.id));

  // Get tension breakdown from edge data (if available from calculate tension)
  const tensionBreakdown = (edge as any).tensionBreakdown;
  
  // Get contributing frictions from edge data
  const contributingFrictions = edge.contributingFrictions || [];
  
  // Deduplicate frictions by ID to avoid showing the same friction multiple times
  const uniqueFrictions = contributingFrictions.reduce((acc: any[], f: any) => {
    if (!acc.find(existing => existing.id === f.id)) {
      acc.push(f);
    }
    return acc;
  }, []);
  
  // Separate frictions by type and target using backend-provided metadata
  const sourceFrictions: any[] = [];
  const targetFrictions: any[] = [];
  const directRelationshipFrictions: any[] = [];
  
  uniqueFrictions.forEach((f: any) => {
    if (f.frictionType === 'relationship_friction') {
      // Direct relationship frictions affect both directions
      directRelationshipFrictions.push(f);
    } else if (f.frictionType === 'entity_friction') {
      // Use backend-provided targeting metadata
      // Only include if magnitude > 0 (actually applies)
      if (f.targetsSource && (f.magnitude || 0) > 0) {
        sourceFrictions.push(f);
      }
      if (f.targetsTarget && (f.magnitude || 0) > 0) {
        targetFrictions.push(f);
      }
    }
  });

  // Calculate total magnitudes WITHOUT financial impact (frictions are raw values)
  const sourceTotalMagnitude = sourceFrictions.reduce((sum, f) => sum + (f.magnitude || 0), 0);
  const targetTotalMagnitude = targetFrictions.reduce((sum, f) => sum + (f.magnitude || 0), 0);
  const directTotalMagnitude = directRelationshipFrictions.reduce((sum, f) => sum + (f.magnitude || 0), 0);

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h4 style={{ margin: 0 }}>Relationship Details</h4>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            padding: '4px 8px',
          }}
        >
          ×
        </button>
      </div>

      {/* Entities */}
      <div style={{ marginBottom: '16px', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
        <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
          {sourceNode?.name || 'Unknown'}
        </div>
        <div style={{ fontSize: '20px', textAlign: 'center', margin: '8px 0', color: '#666' }}>
          ↓
        </div>
        <div style={{ fontSize: '14px', fontWeight: '600' }}>
          {targetNode?.name || 'Unknown'}
        </div>
      </div>

      <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
        ID: {edge.id}
      </div>

      {/* Relationship Type */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Relationship Type</div>
        <div style={{ fontSize: '14px' }}>{edge.relationshipType || 'Not specified'}</div>
      </div>

      {/* Financial Impact */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Financial Impact</div>
        <div style={{ fontSize: '18px', fontWeight: '700', color: '#10b981' }}>
          ${edge.financialImpact.toLocaleString()}
        </div>
      </div>

      {/* Tension with Breakdown */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Tension</div>
        
        {/* Source to Target */}
        <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px', marginBottom: '12px' }}>
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
              {sourceNode?.name} → {targetNode?.name}
            </div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: edge.tension.sourceToTarget > 7 ? '#ef4444' : edge.tension.sourceToTarget > 4 ? '#f59e0b' : '#22c55e' }}>
              {edge.tension.sourceToTarget.toFixed(2)}
            </div>
          </div>
          
          {/* Tension Breakdown */}
          {tensionBreakdown?.sourceToTarget && (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', fontSize: '11px' }}>
              <div style={{ fontWeight: '600', marginBottom: '4px', color: '#666' }}>Breakdown:</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>Direct Relationship Frictions:</span>
                <span style={{ fontWeight: '600' }}>+{tensionBreakdown.sourceToTarget.directRelationshipFrictions.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>Upstream Entity Frictions:</span>
                <span style={{ fontWeight: '600' }}>+{tensionBreakdown.sourceToTarget.upstreamEntityFrictions.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>Propagated from Network:</span>
                <span style={{ fontWeight: '600' }}>+{tensionBreakdown.sourceToTarget.propagatedFromNetwork.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #d1d5db', fontWeight: '600' }}>
                <span>Total:</span>
                <span>{tensionBreakdown.sourceToTarget.total.toFixed(2)}</span>
              </div>
            </div>
          )}
          
          {/* Risk Score */}
          {tensionResult?.relationshipRiskScores?.[edge.id] !== undefined && (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', fontSize: '11px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: '600', color: '#666' }}>Risk Score:</span>
                <span style={{ 
                  fontWeight: '600', 
                  fontSize: '13px',
                  color: tensionResult.relationshipRiskScores[edge.id] > 10 ? '#ef4444' : 
                         tensionResult.relationshipRiskScores[edge.id] > 5 ? '#f59e0b' : '#22c55e'
                }}>
                  {tensionResult.relationshipRiskScores[edge.id].toFixed(2)}
                </span>
              </div>
              <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>
                (Tension × Financial Impact)
              </div>
            </div>
          )}
          
          {/* Contributing Factors with Details */}
          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '6px' }}>
              Detailed Friction List:
            </div>
            {uniqueFrictions.length > 0 ? (
              <div style={{ fontSize: '11px', color: '#666' }}>
                {/* Source entity frictions */}
                {sourceFrictions.length > 0 && (
                  <div style={{ marginBottom: '6px', padding: '6px', background: '#f9fafb', borderRadius: '4px', border: '1px solid #d1d5db' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                      {sourceNode?.name} Frictions ({sourceFrictions.length})
                    </div>
                    {sourceFrictions.map((friction, idx) => {
                      const sign = friction.direction === 'positive' ? '-' : '+';
                      return (
                        <div key={idx} style={{ marginBottom: '2px', paddingLeft: '8px' }}>
                          • {friction.name}: <span style={{ fontWeight: '600' }}>{sign}{(friction.magnitude || 0).toFixed(2)}</span>
                        </div>
                      );
                    })}
                    <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #d1d5db', fontWeight: '600' }}>
                      Total: +{sourceTotalMagnitude.toFixed(2)}
                    </div>
                  </div>
                )}
                
                {/* Direct relationship frictions - detailed */}
                {directRelationshipFrictions.length > 0 && (
                  <div style={{ marginBottom: '6px', padding: '6px', background: '#f9fafb', borderRadius: '4px', border: '1px solid #d1d5db' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                      Direct Relationship Frictions ({directRelationshipFrictions.length})
                    </div>
                    {directRelationshipFrictions.map((friction, idx) => {
                      const sign = friction.direction === 'positive' ? '-' : '+';
                      const magnitude = friction.magnitude || 0;
                      return (
                        <div key={idx} style={{ marginBottom: '2px', paddingLeft: '8px' }}>
                          • {friction.name}: <span style={{ fontWeight: '600' }}>{sign}{magnitude.toFixed(2)}</span>
                        </div>
                      );
                    })}
                    <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #d1d5db', fontWeight: '600' }}>
                      Total: +{directTotalMagnitude.toFixed(2)}
                    </div>
                  </div>
                )}
                
                {sourceFrictions.length === 0 && directRelationshipFrictions.length === 0 && (
                  <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#999' }}>
                    No frictions affecting this direction
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#999' }}>
                Click "Calculate Tension" to see contributing factors
              </div>
            )}
          </div>
        </div>

        {/* Target to Source */}
        <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
              {targetNode?.name} → {sourceNode?.name}
            </div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: edge.tension.targetToSource > 7 ? '#ef4444' : edge.tension.targetToSource > 4 ? '#f59e0b' : '#22c55e' }}>
              {edge.tension.targetToSource.toFixed(2)}
            </div>
          </div>
          
          {/* Tension Breakdown */}
          {tensionBreakdown?.targetToSource && (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', fontSize: '11px' }}>
              <div style={{ fontWeight: '600', marginBottom: '4px', color: '#666' }}>Breakdown:</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>Downstream Entity Frictions:</span>
                <span style={{ fontWeight: '600' }}>+{tensionBreakdown.targetToSource.downstreamEntityFrictions.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>Propagated from Network:</span>
                <span style={{ fontWeight: '600' }}>+{tensionBreakdown.targetToSource.propagatedFromNetwork.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #d1d5db', fontWeight: '600' }}>
                <span>Total:</span>
                <span>{tensionBreakdown.targetToSource.total.toFixed(2)}</span>
              </div>
            </div>
          )}
          
          {/* Contributing Factors with Details */}
          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '6px' }}>
              Contributing Factors:
            </div>
            {contributingFrictions.length > 0 ? (
              <div style={{ fontSize: '11px', color: '#666' }}>
                {/* Target entity frictions */}
                {targetFrictions.length > 0 && (
                  <div style={{ marginBottom: '6px', padding: '6px', background: '#f9fafb', borderRadius: '4px', border: '1px solid #d1d5db' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                      {targetNode?.name} Frictions ({targetFrictions.length})
                    </div>
                    {targetFrictions.map((friction, idx) => {
                      const sign = friction.direction === 'positive' ? '-' : '+';
                      return (
                        <div key={idx} style={{ marginBottom: '2px', paddingLeft: '8px' }}>
                          • {friction.name}: <span style={{ fontWeight: '600' }}>{sign}{(friction.magnitude || 0).toFixed(2)}</span>
                        </div>
                      );
                    })}
                    <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #d1d5db', fontWeight: '600' }}>
                      Total: +{targetTotalMagnitude.toFixed(2)}
                    </div>
                  </div>
                )}
                
                {/* Note: Direct relationship frictions are shown in the source→target section above since they affect both directions */}
                
                {targetFrictions.length === 0 && (
                  <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#999' }}>
                    No entity-specific frictions affecting this direction
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#999' }}>
                Click "Calculate Tension" to see contributing factors
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
